import { useEffect, useRef } from "react";

/**
 * Pledge.to donation embed — locked to a single-nonprofit, currency-only view.
 *
 * The Pledge widget script (loaded in /public/index.html) auto-scans for
 * `.plg-donate` divs and injects the donation UI. When we mount this
 * component after the initial page load — or re-mount it on route change —
 * we re-run the scan.
 *
 * We inject scoped CSS to hide the widget's Split-donation dropdown, the
 * "Multiple nonprofits" chip, and the DAFPay / Crypto / Stock payment tabs
 * so donors can only give as Currency (card / PayPal / Venmo / bank).
 */
export default function PledgeWidget({ widgetId = "1momPQddqEGhNR9B" }) {
  const ref = useRef(null);

  useEffect(() => {
    const rescan = () => {
      const plg = window.PledgeWidgets || window.pledge;
      try {
        if (plg && typeof plg.scan === "function") plg.scan();
        else if (window.PledgeWidgets?.init) window.PledgeWidgets.init();
      } catch (err) {
        console.warn("PledgeWidget: scan failed:", err);
      }
    };
    rescan();
    const t1 = setTimeout(rescan, 400);
    const t2 = setTimeout(rescan, 1200);

    // Watch for Pledge's iframe injection and add currency=USD + paymentMethods=currency
    // to its src as a URL param — Pledge's own script reads these on load and hides the
    // other payment tabs (DAF / Crypto / Stock) inside the iframe. This is our only lever
    // for cross-origin iframe content since outer CSS can't reach inside.
    const container = ref.current;
    if (!container) return () => { clearTimeout(t1); clearTimeout(t2); };

    const patchIframe = (iframe) => {
      if (!iframe || iframe.dataset.plgLocked === "1") return;
      try {
        const src = new URL(iframe.src);
        const before = src.search;
        src.searchParams.set("currency", "USD");
        src.searchParams.set("paymentMethods", "currency");
        src.searchParams.set("lockCurrency", "true");
        src.searchParams.set("hidePaymentTabs", "true");
        if (src.search !== before) iframe.src = src.toString();
        iframe.dataset.plgLocked = "1";
      } catch (err) { console.warn("PledgeWidget: iframe src patch skipped:", err); }
    };
    // Patch any iframe that's already there
    container.querySelectorAll("iframe").forEach(patchIframe);
    // Watch for future iframe injections by Pledge's script
    const mo = new MutationObserver(() => {
      container.querySelectorAll("iframe").forEach(patchIframe);
    });
    mo.observe(container, { childList: true, subtree: true });

    return () => { clearTimeout(t1); clearTimeout(t2); mo.disconnect(); };
  }, []);

  return (
    <>
      {/* Scoped CSS — hide Pledge's non-currency payment tabs + split-donation UI */}
      <style>{`
        /* Payment-method tabs (Currency / DAFPay / Crypto / Stock) */
        .plg-donate [class*="payment-method-tab"],
        .plg-donate [class*="PaymentMethodTab"],
        .plg-donate [class*="paymentMethodTab"],
        .plg-donate [data-tab="dafpay"],
        .plg-donate [data-tab="crypto"],
        .plg-donate [data-tab="stock"],
        .plg-donate button[aria-label*="DAF" i],
        .plg-donate button[aria-label*="Crypto" i],
        .plg-donate button[aria-label*="Stock" i] {
          display: none !important;
        }
        /* Split-donation dropdown ("Multiple nonprofits") */
        .plg-donate [class*="nonprofit-selector"],
        .plg-donate [class*="NonprofitSelector"],
        .plg-donate [class*="split-donation"],
        .plg-donate [class*="SplitDonation"],
        .plg-donate [class*="split_donation"],
        .plg-donate [aria-label*="Split" i],
        .plg-donate [aria-label*="nonprofit" i]:not([aria-label*="Give" i]),
        .plg-donate select[name*="nonprofit"],
        .plg-donate select[id*="nonprofit"] {
          display: none !important;
        }
        /* Hide the entire tab strip if it contains only the non-currency tabs */
        .plg-donate [role="tablist"]:not(:has([data-tab="currency"])) {
          display: none !important;
        }
        /* Hide the currency selector — donations are locked to USD */
        .plg-donate [class*="currency-selector"],
        .plg-donate [class*="CurrencySelector"],
        .plg-donate select[name*="currency" i],
        .plg-donate select[id*="currency" i],
        .plg-donate [aria-label*="currency" i] {
          display: none !important;
        }
        /* Keep the widget itself full-width and readable */
        .plg-donate iframe { width: 100% !important; min-height: 620px; border: 0; }
      `}</style>
      <div
        ref={ref}
        data-testid="pledge-widget"
        className="plg-donate bg-white"
        data-widget-id={widgetId}
        data-payment-methods="currency"
        data-currency="USD"
        data-default-currency="USD"
        data-lock-currency="true"
        data-hide-tabs="true"
        data-allow-split="false"
        data-lock-recipients="true"
        style={{ minHeight: 620 }}
      />
    </>
  );
}
